import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { ImportStatus, Import, ImportType } from "types/Import";
import dayjs from "dayjs";
import { GiFamilyTree } from "react-icons/gi";
import { IoPaw } from "react-icons/io5";
import { TableCellsIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo } from "react";
import useRouter from "common/hooks/use-router";
import { FiBox } from "react-icons/fi";
import Button from "common/components/Button/Button";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import contentMapping from "./utils/contentMapping";

function ItemLogo(key: ImportType) {
  switch (key) {
    case ImportType.ZIMS:
      return <IoPaw className="w-5 h-5 mr-2" />;
    case ImportType.OBSERVATION_GRID:
      return <TableCellsIcon className="w-5 h-5 mr-2" />;
    case ImportType.TRAINING:
      return <FiBox className="w-5 h-5 mr-2" />;
    default:
      return <GiFamilyTree className="w-5 h-5 mr-2" />;
  }
}

const getColorFromItem = (action: ImportStatus) => {
  switch (action) {
    case ImportStatus.COMPLETE:
      return "border-green-500 border-4 border-double";
    case ImportStatus.ERROR:
    case ImportStatus.COMPLETE_WITH_ERRORS:
      return "border-red-500 border-4 border-double";
    case ImportStatus.IN_PROGRESS:
      return "border-blue-500 border-4 border-double";
    case ImportStatus.CANCELED:
      return "border-primary-500 border-4 border-double";
    case ImportStatus.INCOMING:
      return "border-slate-300 border-4 border-double";
    default:
      return "border-slate-150 border-4 border-double";
  }
};

function ImportItem({ item, onNavigate, onCancelImport }: any) {
  const { id, fileName, author, status, importType, createdAt, parsedContent } =
    item as Import;

  const { t } = useTranslation();
  const { navigate } = useRouter();

  // -- Specific code here --

  const handleNavigate = useCallback(() => {
    if (importType === ImportType.ZIMS && status !== ImportStatus.ERROR) {
      onNavigate(item.id);
    } else if (
      importType === ImportType.OBSERVATION_GRID &&
      (status === ImportStatus.INCOMING || status === ImportStatus.IN_PROGRESS)
    ) {
      navigate(`/imports/${id}/observation-grids/add`);
    } else if (
      importType === ImportType.TRAINING &&
      (status === ImportStatus.INCOMING ||
        status === ImportStatus.IN_PROGRESS) &&
      parsedContent &&
      parsedContent.data
    ) {
      navigate(`/imports/${id}/training-exercises/add`);
    }
  }, [id, importType, item.id, navigate, onNavigate, parsedContent, status]);

  const clanClick = useMemo(() => {
    if (
      (importType === ImportType.OBSERVATION_GRID ||
        importType === ImportType.TRAINING) &&
      (status === ImportStatus.INCOMING || status === ImportStatus.IN_PROGRESS)
    ) {
      return true;
    }
    if (importType === ImportType.ZIMS && status !== ImportStatus.ERROR) {
      return true;
    }
    return false;
  }, [importType, status]);

  const SpecificContent = contentMapping[importType];

  // ------------------------

  const containerClassName = clsx(
    "flex px-4 py-4 mt-2 w-full relative bg-white rounded-lg border border-slate-150 transition-all",
    getColorFromItem(status.toLowerCase() as ImportStatus),
    clanClick && "cursor-pointer"
  );

  return (
    <div className={containerClassName} onClick={handleNavigate} key={id}>
      <div className="flex flex-col flex-1 justify-between pl-4 pr-6 mb-2 mt-2">
        <div className="flex-1 flex flex-row justify-between items-center">
          <div className="flex items-center text-sm">
            {ItemLogo(importType)}
            <h4 className="text-xl font-bold text-primary leading-4">
              {fileName}
            </h4>
          </div>
          {SpecificContent && <SpecificContent item={item} />}
        </div>
      </div>
      <div className="flex flex-col justify-end items-end text-xs lg:border-l lg:pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <div className="flex w-full items-center justify-end text-slate-500 h-full">
          <div className="flex flex-col items-start justify-center w-full h-full">
            <span className="font-bold">{dayjs(createdAt).format("LLLL")}</span>
            <div>
              <span className="mr-2">{t("import.importedBy")}</span>
              <span className="font-bold">
                {author?.username ?? t("common.unnamed")}
              </span>
            </div>
          </div>
          {status === ImportStatus.INCOMING && (
            <TooltipWrapper
              key={item.id}
              className="mr-1"
              content={<span>{t("import.cancel")}</span>}
            >
              <Button
                type="neutral-line"
                icon="XMarkIcon"
                onClick={(event) => {
                  event.stopPropagation();
                  onCancelImport(item);
                }}
              />
            </TooltipWrapper>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImportItem;
